import { useState } from 'react';
import { useSmsConversations, useSendSms } from '../sms/useSmsConversations';
import { useSms } from '../store/sms';
import { SmsChatPanel } from '../sms/SmsChatPanel';
import { relativeTime } from '../lib/utils';
import type { SmsConversationStatus } from '@marnic/shared';

const STATUS_TABS: SmsConversationStatus[] = ['open', 'snoozed', 'closed'];

export function Sms(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<SmsConversationStatus>('open');
  const [composeOpen, setComposeOpen] = useState(false);

  const { data, isLoading } = useSmsConversations({ status: statusFilter });
  const items = data?.items ?? [];

  const activeId = useSms((s) => s.activeConversationId);
  const setActive = useSms((s) => s.setActiveConversation);

  const selected = items.find((c) => c.id === activeId) ?? null;

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">SMS</h1>
        <div className="flex items-center gap-2">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={statusFilter === s ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button type="button" className="btn-primary text-xs" onClick={() => setComposeOpen(true)}>
            ✎ New SMS
          </button>
        </div>
      </header>

      <div className="grid gap-4 lg:grid-cols-[1fr_420px]">
        {/* Left panel — conversation list */}
        <div className="card overflow-hidden p-0">
          {isLoading && <div className="px-4 py-8 text-center text-sm text-ink-400">Loading…</div>}
          {!isLoading && items.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-ink-400">No {statusFilter} conversations.</div>
          )}
          <ul className="divide-y divide-ink-100">
            {items.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setActive(c.id === activeId ? null : c.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-ink-50 transition-colors ${
                    c.id === activeId ? 'bg-brand-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-sm truncate">
                      {c.lead?.firstName} {c.lead?.lastName}
                      {c.lead?.company ? <span className="text-ink-500 font-normal"> · {c.lead.company}</span> : null}
                    </span>
                    <span className="text-xs text-ink-400 shrink-0 mt-0.5">
                      {c.lastMessageAt ? relativeTime(c.lastMessageAt) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-ink-500 font-mono">{c.lead?.phoneE164 ?? '—'}</span>
                    {c.unreadCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-brand-600 text-white text-xs font-semibold">
                        {c.unreadCount > 99 ? '99+' : c.unreadCount}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Right panel — chat */}
        <div>
          <SmsChatPanel conversation={selected} />
        </div>
      </div>

      {composeOpen && (
        <NewSmsModal
          onClose={() => setComposeOpen(false)}
          onSent={(conversationId) => {
            setComposeOpen(false);
            setStatusFilter('open');
            setActive(conversationId);
          }}
        />
      )}
    </div>
  );
}

function NewSmsModal({
  onClose,
  onSent,
}: {
  onClose: () => void;
  onSent: (conversationId: string) => void;
}): JSX.Element {
  const [phone, setPhone] = useState('');
  const [body, setBody] = useState('');
  const send = useSendSms();

  const submit = (): void => {
    if (!phone.trim() || !body.trim()) return;
    send.mutate(
      { phone: phone.trim(), body: body.trim() },
      { onSuccess: (res) => onSent(res.conversationId) },
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-ink-900/40 p-4">
      <div className="card w-full max-w-md p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">New SMS</h3>
          <button type="button" className="btn-ghost" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div>
          <label className="label">To (number)</label>
          <input
            className="input font-mono"
            placeholder="+44…"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <p className="mt-1 text-[11px] text-ink-500">The number must belong to an existing lead.</p>
        </div>
        <div>
          <label className="label">Message</label>
          <textarea
            className="input min-h-[90px]"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        {send.isError && (
          <p className="text-xs text-red-600">
            Could not send — the number may not match a lead, or isn't SMS-capable.
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={send.isPending || !phone.trim() || !body.trim()}
          >
            {send.isPending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}
